import { useEffect, useRef } from "react";

export type WakeReason = "visible" | "online" | "drift" | "resume";
export type WakeInfo = { reason: WakeReason; hiddenMs: number };

// A setInterval tick that lands this much later than scheduled means the timer (and the tab) was almost certainly frozen — OS sleep / tab discard — rather than the normal sub-second jitter or background-tab throttling.
const DRIFT_TICK_MS = 15_000;
const DRIFT_THRESHOLD_MS = 30_000;

/**
 * Fires `onWake` when the tab resumes after being backgrounded or the machine
 * wakes from sleep. Charts run on long-lived streaming fetches that an OS suspend
 * can leave half-open — the socket is dead but `reader.read()` never returns — so
 * the query sits "fetching" forever and the browser's own focus/reconnect
 * refetching can't recover it. This gives charts one explicit "we just woke up"
 * signal to reconnect on.
 *
 * Three independent triggers, because no single one covers every suspend path:
 *  - `visibilitychange` → visible: the common "switched away then back" / tab restore.
 *  - `online`: a dropped-then-restored connection (sleep usually drops the socket).
 *  - interval drift: catches a suspend where the tab stayed *visible* the whole
 *    time (e.g. laptop lid closed on the focused tab) so no visibility event fires.
 *
 * `minHiddenMs` lets a caller ignore brief tab switches (recover only after a real,
 * multi-second gap). It gates the `visible` and `drift` triggers; an `online` event
 * always fires since a reconnect is always worth reacting to.
 */
export const useOnWake = (onWake: (info: WakeInfo) => void, { minHiddenMs = 0 }: { minHiddenMs?: number } = {}) => {
  const cbRef = useRef(onWake);
  cbRef.current = onWake;

  useEffect(() => {
    let hiddenAt: number | null = document.visibilityState === "hidden" ? Date.now() : null;
    let frozenAt: number | null = null;

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const hiddenMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (hiddenMs >= minHiddenMs) cbRef.current({ reason: "visible", hiddenMs });
    };

    const onOnline = () => cbRef.current({ reason: "online", hiddenMs: 0 });

    const onFreeze = () => {
      frozenAt = Date.now();
    };

    const onResume = () => {
      const hiddenMs = frozenAt === null ? 0 : Date.now() - frozenAt;
      frozenAt = null;
      if (hiddenMs >= minHiddenMs) cbRef.current({ reason: "resume", hiddenMs });
    };

    let lastTick = Date.now();
    const driftTimer = setInterval(() => {
      const now = Date.now();
      const drift = now - lastTick - DRIFT_TICK_MS;
      lastTick = now;
      // Only treat drift as a wake while visible: a backgrounded tab is throttled to ~1 tick/min, which would otherwise look like constant "drift". The hidden → visible path handles waking from a backgrounded state.
      if (document.visibilityState !== "visible") return;
      if (drift >= DRIFT_THRESHOLD_MS && drift >= minHiddenMs) {
        cbRef.current({ reason: "drift", hiddenMs: drift + DRIFT_TICK_MS });
      }
    }, DRIFT_TICK_MS);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    document.addEventListener("freeze", onFreeze);
    document.addEventListener("resume", onResume);
    return () => {
      clearInterval(driftTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("freeze", onFreeze);
      document.removeEventListener("resume", onResume);
    };
  }, [minHiddenMs]);
};
